/**
 * Worksite feed hub — single entry point reachable from the admin AND employee
 * home screens. Shows a list of accessible projects ("chantiers"); selecting
 * one reveals the WorksiteFeedTab below for that chantier.
 *
 * - Admin : sees all projects (GET /api/projects).
 * - Employee : sees their assigned projects (GET /api/users/me/projects).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Buildings, MagnifyingGlass, NewspaperClipping } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { WorksiteFeedTab } from '../components/projects/WorksiteFeedTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorksiteFeedHubPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const isAdmin = user?.role === 'admin';
  const selectedId = params.get('project');
  const selected = projects.find((p) => p.id === selectedId);

  useEffect(() => {
    const run = async () => {
      try {
        const endpoint = isAdmin ? '/api/projects' : '/api/users/me/projects';
        const res = await axios.get(`${API_URL}${endpoint}`, { withCredentials: true });
        // Backend returns an array of {id, name, status, address?, ...}
        const list = (res.data || []).map((p) => ({
          id: p.id || p._id,
          name: p.name || p.project_name || 'Sans nom',
          status: p.status || '',
          address: p.address || '',
        }));
        // Active first
        list.sort((a, b) => {
          const aActive = a.status === 'in_progress' ? 0 : 1;
          const bActive = b.status === 'in_progress' ? 0 : 1;
          if (aActive !== bActive) return aActive - bActive;
          return (a.name || '').localeCompare(b.name || '');
        });
        setProjects(list);
      } catch {
        toast.error('Impossible de charger les chantiers');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.address || '').toLowerCase().includes(q),
    );
  }, [projects, query]);

  const selectProject = (id) => {
    setParams({ project: id });
  };
  const back = () => {
    setParams({});
  };

  if (loading) {
    return (
      <div style={{ padding: 24, color: '#5E5F66' }} data-testid="worksite-feed-hub-loading">
        Chargement des chantiers...
      </div>
    );
  }

  // --- Detail view : the WorksiteFeedTab for the selected project --------
  if (selected) {
    return (
      <div data-testid="worksite-feed-hub-detail" style={{ padding: '10px 12px 80px', maxWidth: 820, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0 14px' }}>
          <button
            onClick={back}
            data-testid="worksite-feed-back"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center', color: '#0F172A' }}
            aria-label="Retour"
          >
            <ArrowLeft size={22} weight="bold" />
          </button>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: '0.7rem', color: '#5E5F66', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Fil d'actualité
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: '1.15rem', fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {selected.name}
            </h1>
          </div>
        </div>
        <WorksiteFeedTab project={selected} currentUser={user} />
      </div>
    );
  }

  // --- Hub view : list of selectable chantiers ---------------------------
  return (
    <div data-testid="worksite-feed-hub" style={{ padding: '10px 12px 80px', maxWidth: 820, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0 16px' }}>
        <button
          onClick={() => navigate('/')}
          data-testid="worksite-feed-home"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center', color: '#0F172A' }}
          aria-label="Accueil"
        >
          <ArrowLeft size={22} weight="bold" />
        </button>
        <div>
          <div style={{ fontSize: '0.7rem', color: '#5E5F66', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Fil d'actualité
          </div>
          <h1 style={{ margin: '2px 0 0', fontSize: '1.3rem', fontWeight: 700, color: '#0F172A' }}>
            Choisir un chantier
          </h1>
        </div>
      </div>

      <div style={{ position: 'relative', marginBottom: 14 }}>
        <MagnifyingGlass size={18} style={{ position: 'absolute', top: 12, left: 12, color: '#5E5F66' }} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un chantier..."
          data-testid="worksite-feed-search"
          className="form-input"
          style={{ padding: '10px 12px 10px 38px' }}
        />
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: '24px 12px', textAlign: 'center', color: '#5E5F66', background: '#F4F5F7', borderRadius: 10 }}>
          <NewspaperClipping size={42} weight="duotone" style={{ color: '#9E9EA7' }} />
          <p style={{ marginTop: 8 }}>{query ? 'Aucun chantier trouvé.' : (isAdmin ? 'Pas encore de chantier.' : 'Vous n\'êtes assigné à aucun chantier actif.')}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`worksite-feed-project-${p.id}`}
              onClick={() => selectProject(p.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                background: 'white', border: '1px solid #E2E2E6', borderRadius: 12,
                padding: '12px 14px', cursor: 'pointer', textAlign: 'left',
                width: '100%', boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
              }}
            >
              <span style={{
                width: 44, height: 44, borderRadius: 12,
                background: 'linear-gradient(135deg, #0891B2, #22D3EE)',
                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Buildings size={22} weight="duotone" />
              </span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {p.name}
                </div>
                {p.address && (
                  <div style={{ fontSize: '0.76rem', color: '#5E5F66', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {p.address}
                  </div>
                )}
              </span>
              {p.status === 'in_progress' && (
                <span style={{ fontSize: '0.66rem', background: '#16A34A', color: 'white', padding: '3px 8px', borderRadius: 8, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                  Actif
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorksiteFeedHubPage;
